import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  SupervisorMonitorSideBar,
  SUPERVISOR_MONITOR_LIST_MODULE,
  SIDEBAR_TEXT,
  DelegateList,
  PolishCommentsList,
  SupervisorScholarshipManagementList,
  SupervisorApplicationListV2,
} from './components';
import { SupervisorNonProposedList } from './components/SupervisorMonitorList/ApplicationList/SupervisorNonProposedList';
import { CoSupervisorList } from './components/SupervisorMonitorList/ApplicationList/CoSupervisorList';
import { supervisorPolishCommentsFilter } from './components/filters';
import { useAuth } from '../../contexts/authContext/authContext';

const delegationModeOn = process.env.REACT_APP_DELEGATION_ON === 'true';
export const SupervisorMonitorList: React.FunctionComponent = () => {
  const { user } = useAuth();
  const delegateMode = user?.sub !== user?.userInfo?.netId && delegationModeOn;

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  // am means supervisor SideBar
  let sidebarActivated = searchParams.get(
    'am',
  ) as SUPERVISOR_MONITOR_LIST_MODULE;

  if (
    !Object.values(SUPERVISOR_MONITOR_LIST_MODULE).includes(sidebarActivated)
  ) {
    sidebarActivated = SUPERVISOR_MONITOR_LIST_MODULE.APPLICATION_LIST;
  }
  const [activatedModuleId, setActivatedModuleId] = useState(sidebarActivated);

  useEffect(() => {
    const keys = Array.from(searchParams.keys());
    // delete unnecessary param
    keys.forEach(key => {
      if (key !== 'am') {
        searchParams.delete(key);
      }
    });

    // reset unreachable param
    if (!supervisorMonitorListModulesMap[activatedModuleId]?.reachable) {
      searchParams.set('am', SUPERVISOR_MONITOR_LIST_MODULE.APPLICATION_LIST);
    } else {
      searchParams.set('am', activatedModuleId);
    }

    // Update the URL with new Module
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  }, [activatedModuleId, location.pathname]);

  const userNetId = user?.userInfo?.netId ?? null;

  const supervisorMonitorListModulesMap: Partial<
    Record<
      SUPERVISOR_MONITOR_LIST_MODULE,
      { component: React.ReactNode; moduleTitle: string; reachable: boolean }
    >
  > = {
    [SUPERVISOR_MONITOR_LIST_MODULE.APPLICATION_LIST]: {
      component: <SupervisorApplicationListV2 />,
      moduleTitle: SIDEBAR_TEXT.APPLICATION_LIST,
      reachable: true,
    },
    [SUPERVISOR_MONITOR_LIST_MODULE.NON_PROPOSED_APPLICATION]: {
      component: <SupervisorNonProposedList />,
      moduleTitle: SIDEBAR_TEXT.NON_PROPOSED_APPLICATION,
      reachable: true,
    },
    [SUPERVISOR_MONITOR_LIST_MODULE.CO_SUPERVISOR_DECLARATION]: {
      component: <CoSupervisorList />,
      moduleTitle: SIDEBAR_TEXT.CO_SUPERVISOR_DECLARATION,
      reachable: true,
    },
    [SUPERVISOR_MONITOR_LIST_MODULE.POLISH_COMMENTS]: {
      component: (
        <PolishCommentsList
          filter={supervisorPolishCommentsFilter(userNetId)}
        />
      ),
      moduleTitle: SIDEBAR_TEXT.SHORTLISTED_HKPFS,
      reachable: true,
    },
    [SUPERVISOR_MONITOR_LIST_MODULE.SCHOLARSHIP_MANAGEMENT]: {
      component: <SupervisorScholarshipManagementList />,
      moduleTitle: SIDEBAR_TEXT.SCHOLARSHIP_MANAGEMENT,
      reachable: true,
    },

    ...(delegateMode && {
      [SUPERVISOR_MONITOR_LIST_MODULE.DELEGATE_LIST]: {
        component: <DelegateList />,
        moduleTitle: SIDEBAR_TEXT.DELEGATE_LIST,
        reachable: delegateMode,
      },
    }),
  };
  const activatedModule:
    | { component: React.ReactNode; moduleTitle: string }
    | undefined = supervisorMonitorListModulesMap[activatedModuleId];

  const moduleComponent = activatedModule?.component ?? activatedModuleId;
  const moduleTitle = activatedModule?.moduleTitle ?? activatedModuleId;

  const handleChangeActivated = (
    activatedId: SUPERVISOR_MONITOR_LIST_MODULE,
  ): void => {
    setActivatedModuleId(activatedId);
    window.scrollTo(0, 0);
  };

  return (
    <PageContainer>
      <div className="sidebar-area">
        <SupervisorMonitorSideBar
          onChangeActivated={handleChangeActivated}
          collapsible={true}
          activatedModule={activatedModuleId}
        />
      </div>
      <Container>
        <h1>{moduleTitle}</h1>
        <div className="form-area">{moduleComponent}</div>
      </Container>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  display: flex;
  flex-direction: row;
  h1 {
    margin-top: 2rem;
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    text-align: center;
    color: #951d1d;
  }
  .sidebar-area {
    width: min-content;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 70%;

  .form-area {
    margin: 1rem;
  }

  .submit-area {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 0.5rem 1rem;
    margin-top: 2rem;
    button {
      margin: 0 1rem;
    }
  }
`;
